/**
 * IntentionScreen — mandatory step before every retreat start.
 *
 * UI-INV-005: Leaf component — props only, no engine imports.
 * screen-integration-005
 */
import { useState, useMemo } from "react";
import type { IntentionCategory, Intention, IntentionRecord } from "@retreats/content";
import styles from "./IntentionScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { PrimaryAction } from "../primitives/PrimaryAction";

type Props = {
  retreatName: string;
  categories: IntentionCategory[];
  intentions: Intention[];
  onConfirm: (record: IntentionRecord) => void;
  onDecline: () => void;
};

export function IntentionScreen({ retreatName, categories, intentions, onConfirm, onDecline }: Props) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filterText, setFilterText] = useState("");

  const filteredIntentions = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return intentions;
    return intentions.filter(i => i.label.toLowerCase().includes(q));
  }, [intentions, filterText]);

  function toggleId(id: string) {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  }

  function handleConfirm() {
    const trimmed = filterText.trim();
    const record: IntentionRecord = {
      kind: "set",
      intentionIds: selectedIds,
      customText: selectedIds.length === 0 && trimmed ? trimmed : undefined,
      setAt: Date.now(),
    };
    onConfirm(record);
  }

  return (
    <div className={styles.screen} data-testid={TEST_IDS.INTENTION_SCREEN}>
      <div className={styles.content}>
        <p className={styles.eyebrow}>{retreatName}</p>
        <h1 className={styles.heading}>What are you sitting with?</h1>
        <p className={styles.subhead}>There is no wrong answer. You can also say nothing.</p>

        <input
          className={styles.filterInput}
          type="text"
          placeholder="Search or write your own…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          aria-label="Search intentions or write your own"
        />

        {categories.map((cat) => {
          const items = filteredIntentions.filter((i) => i.category === cat.id);
          if (items.length === 0) return null;
          return (
            <div key={cat.id} className={styles.group}>
              <div className={styles.groupLabel}>{cat.label}</div>
              <div className={styles.chips}>
                {items.map((item) => {
                  const active = selectedIds.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`${styles.chip} ${active ? styles.chipSelected : ""}`}
                      onClick={() => toggleId(item.id)}
                      aria-pressed={active}
                      data-testid={TEST_IDS.INTENTION_OPTION}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        <div className={styles.ctas}>
          <PrimaryAction onClick={handleConfirm} testId={TEST_IDS.CONFIRM_INTENTION_CTA}>Begin retreat</PrimaryAction>
          <button
            className={styles.skipLink}
            type="button"
            onClick={onDecline}
            data-testid={TEST_IDS.SKIP_INTENTION_CTA}
          >
            I&rsquo;d rather not say
          </button>
        </div>
      </div>
    </div>
  );
}
